import React, { useEffect, useRef, useState } from 'react';
import './styles.css';
import {
  Box,
  Typography,
  ThemeProvider,
  createTheme,
  Tooltip,
  ClickAwayListener,
} from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowsRotate, faBars } from '@fortawesome/free-solid-svg-icons';
import SearchDropBox from '../../components/SearchDropBox';
import logo from '../../assets/icons/JLogoLight.svg';
import { Bell } from 'lucide-react';
import NotificationPopup from '../NotificationsPopup';
import { useTranslation } from 'react-i18next';
import { fetchNotifications, getInstandtNotifications } from '../../api/common';
import { setNotifications } from '../../redux/reducers/common';
import AppDrawer from '../../components/AppDrawer';
import { colors } from '../../common/pallette/colors';

const theme = createTheme({
  components: {},
});

const CurrentClubIndicator = () => {
  const [t] = useTranslation();
  const dispatch: any = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const [rotation, setRotation] = useState(0);
  const switchBtnRef = useRef<HTMLDivElement | null>(null);
  const currentClub = useSelector((state: any) => state.clubDetails);
  const isAdmin = useSelector((state: any) => state.common.isAdmin);
  const [isOpenNotifications, setIsOpenNotifications] = useState(false);
  const [isOpenDrawer, setIsOpenDrawer] = useState(false);
  const user = useSelector((state: any) => state.common.user);
  const notifications = useSelector((state: any) => state.common.notifications);

  const unreadCount = notifications?.filter((n: any) => !n.isRead).length;
  const textColor = currentClub?.textColor ? currentClub.textColor : '#fff';
  const clubBg = currentClub.color ? currentClub.color : '#000';

  const togglePopup = () => setIsOpenNotifications(!isOpenNotifications);

  const toggleClubSwitch = () => {
    setIsOpen((open) => {
      setRotation(open ? 0 : -180);
      return !open;
    });
  };

  const closeClubSwitch = () => {
    setIsOpen(false);
    setRotation(0);
  };

  useEffect(() => {
    user &&
      user?.id &&
      fetchNotifications({ userId: user.id }).then((res: any) => {
        res && res?.length && dispatch((setNotifications as any)(res));
      });
  }, [dispatch, user, currentClub]);

  useEffect(() => {
    let isMounted = true;

    const pollNotifications = async () => {
      try {
        const res = await getInstandtNotifications({ isFetched: false });
        if (isMounted && res?.length) {
          const tempArr: any = [...res, ...notifications];
          dispatch((setNotifications as any)(tempArr));
        }
      } catch (error) {
        console.error('Failed to fetch notifications:', error);
      }
    };

    const interval = setInterval(pollNotifications, 5000);
    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [dispatch]);

  return (
    <React.Fragment>
      <ThemeProvider theme={theme}>
        <NotificationPopup
          isOpen={isOpenNotifications}
          handleClose={() => setIsOpenNotifications(false)}
        />
        <Box
          className={!isOpenDrawer ? 'displayNone' : 'currentClubIndicatorDrawerWrapper'}
        >
          <AppDrawer
            isDrawerOpen={isOpenDrawer}
            setDrawerIsOpen={() => setIsOpenDrawer(!isOpenDrawer)}
          />
        </Box>

        <ClickAwayListener
          onClickAway={(event) => {
            if (!isOpen) return;
            const target = event.target as HTMLElement | null;
            // Autocomplete list renders in a portal — don't treat it as outside
            if (
              target?.closest('.MuiAutocomplete-popper') ||
              target?.closest('.MuiPopper-root')
            ) {
              return;
            }
            closeClubSwitch();
          }}
        >
          <Box className={'currentClubContainerWrapper'} bgcolor={clubBg}>
            <Box className={'currentClubDetail'} bgcolor={clubBg}>
              <Box sx={{ display: 'grid', gridAutoFlow: 'column', alignItems: 'center', gap: '2px' }}>
                <Box
                  className={'hamburgerIconContainer'}
                  onClick={() => setIsOpenDrawer(!isOpenDrawer)}
                >
                  <FontAwesomeIcon
                    style={{ color: textColor, fontSize: '20px' }}
                    icon={faBars}
                  />
                </Box>

                {isAdmin && (
                  <Tooltip
                    title={isOpen ? 'Close club switcher' : 'Switch club'}
                    arrow
                    placement="bottom"
                  >
                    <Box
                      ref={switchBtnRef}
                      className={`changeIconContainer${isOpen ? ' is-active' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleClubSwitch();
                      }}
                      sx={{
                        border: `1.5px solid ${textColor}`,
                        color: textColor,
                      }}
                    >
                      <FontAwesomeIcon
                        className="changeIcon"
                        style={{
                          transform: `rotate(${rotation}deg)`,
                          transition: 'transform 0.35s ease',
                          color: textColor,
                        }}
                        icon={faArrowsRotate}
                      />
                    </Box>
                  </Tooltip>
                )}

                <Tooltip title={t('NOTIFICATIONS')} arrow placement="bottom">
                  <button
                    onClick={togglePopup}
                    style={{
                      position: 'relative',
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '8px',
                      color: textColor,
                      transition: 'color 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Bell size={22} />
                    {unreadCount > 0 && (
                      <span
                        style={{
                          position: 'absolute',
                          top: '0px',
                          right: '0px',
                          backgroundColor: colors.blue.navy,
                          color: '#fff',
                          borderRadius: '50%',
                          padding: '4px',
                          fontSize: '11px',
                          fontWeight: 'bold',
                          textAlign: 'center',
                          width: '11px',
                          height: '11px',
                          lineHeight: '11px',
                        }}
                      >
                        {unreadCount}
                      </span>
                    )}
                  </button>
                </Tooltip>
              </Box>

              <Box className={'clubNameContainer'}>
                <Typography
                  className="clubIndicatorTitle"
                  sx={{ color: textColor }}
                >
                  {currentClub.name}
                </Typography>
                <Box className="logoContainer">
                  <img
                    className="clubIndicatorLogo"
                    src={
                      currentClub.id == 0
                        ? logo
                        : `https://jamp.football${currentClub?.imageUrl || currentClub?.logo}`
                    }
                    alt={'CLUB LOGO'}
                  />
                </Box>
              </Box>
            </Box>

            {/* Club search — slides right → left from under the club bar */}
            {isAdmin && (
              <Box className={`clubSwitchPanel${isOpen ? ' is-open' : ''}`}>
                <SearchDropBox
                  placeholder="Search clubs…"
                  autoFocus={isOpen}
                  selectedValue={() => closeClubSwitch()}
                />
              </Box>
            )}
          </Box>
        </ClickAwayListener>
      </ThemeProvider>
    </React.Fragment>
  );
};

export default CurrentClubIndicator;
